import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';
import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';

type ScreenKeys =
  | 'freezeWindowEmptyScreen'
  | 'freezeWindowSelectAppsScreen'
  | 'freezeWindowSetTimeframeScreen'
  | 'freezeWindowCreateFreezeWindowScreen'
  | 'freezeWindowHomeScreen'
  | 'freezeWindowRemoveModal';

type UiKeys =
  | 'freezeWindowEmptyCreate'
  | 'freezeWindowEmptySupportLink'
  | 'freezeWindowSelectAppButton'
  | 'freezeWindowSelectAppNext'
  | 'freezeWindowSelectAppBack'
  | 'freezeWindowSelectAppExit'
  | 'freezeWindowSetTimeframeStartDate'
  | 'freezeWindowSetTimeframeStartDateCalendar'
  | 'freezeWindowSetTimeframeEndDate'
  | 'freezeWindowSetTimeframeEndDateCalendar'
  | 'freezeWindowSetTimeframeNext'
  | 'freezeWindowSetTimeframeBack'
  | 'freezeWindowSetTimeframeExit'
  | 'freezeWindowCreateFreezeWindowCreate'
  | 'freezeWindowCreateFreezeWindowBack'
  | 'freezeWindowCreateFreezeWindowExit'
  | 'freezeWindowHomeSupportLink'
  | 'freezeWindowHomeRemove'
  | 'freezeWindowHomeRemoveConfirm'
  | 'freezeWindowHomeRemoveCancel'
  | 'freezeWindowHomeCreate'
  | 'freezeWindowHomeView'
  | 'freezeWindowHomeSidebarClose';

type TrackKeys = 'freezeWindowCreateFreezeWindow';

export enum FreezeWindowsScreens {
  freezeWindowEmptyScreen = 'freezeWindowEmptyScreen',
  freezeWindowSelectAppsScreen = 'freezeWindowSelectAppsScreen',
  freezeWindowSetTimeframeScreen = 'freezeWindowSetTimeframeScreen',
  freezeWindowCreateFreezeWindowScreen = 'freezeWindowCreateFreezeWindowScreen',
  freezeWindowHomeScreen = 'freezeWindowHomeScreen',
  freezeWindowRemoveModal = 'freezeWindowRemoveModal',
}

export const freezeWindowsEvents: FeatureEvents<ScreenKeys, UiKeys, TrackKeys, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/CHG/pages/5398108583/Freeze+Windows+Analytics+Spec',

  screen: {
    freezeWindowEmptyScreen: (): ScreenEvent => ({
      data: {
        name: FreezeWindowsScreens.freezeWindowEmptyScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84727',
    }),
    freezeWindowSelectAppsScreen: (): ScreenEvent => ({
      data: {
        name: FreezeWindowsScreens.freezeWindowSelectAppsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84728',
    }),
    freezeWindowSetTimeframeScreen: (): ScreenEvent => ({
      data: {
        name: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84729',
    }),
    freezeWindowCreateFreezeWindowScreen: (): ScreenEvent => ({
      data: {
        name: FreezeWindowsScreens.freezeWindowCreateFreezeWindowScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84730',
    }),
    freezeWindowHomeScreen: (): ScreenEvent => ({
      data: {
        name: FreezeWindowsScreens.freezeWindowHomeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84731',
    }),
    freezeWindowRemoveModal: (): ScreenEvent => ({
      data: {
        name: FreezeWindowsScreens.freezeWindowRemoveModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84733',
    }),
  },

  ui: {
    freezeWindowEmptyCreate: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowEmptyCreate',
        source: FreezeWindowsScreens.freezeWindowEmptyScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84836',
    }),
    freezeWindowEmptySupportLink: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowEmptySupportLink',
        source: FreezeWindowsScreens.freezeWindowEmptyScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84837',
    }),
    freezeWindowSelectAppButton: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSelectAppButton',
        source: FreezeWindowsScreens.freezeWindowSelectAppsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84931',
    }),
    freezeWindowSelectAppNext: (attributes: { freezeWindowSelectionCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSelectAppNext',
        source: FreezeWindowsScreens.freezeWindowSelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84932',
    }),
    freezeWindowSelectAppBack: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSelectAppBack',
        source: FreezeWindowsScreens.freezeWindowSelectAppsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84933',
    }),
    freezeWindowSelectAppExit: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSelectAppExit',
        source: FreezeWindowsScreens.freezeWindowSelectAppsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84934',
    }),
    freezeWindowSetTimeframeStartDate: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSetTimeframeStartDate',
        source: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84987',
    }),
    freezeWindowSetTimeframeStartDateCalendar: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSetTimeframeStartDateCalendar',
        source: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84988',
    }),
    freezeWindowSetTimeframeEndDate: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSetTimeframeEndDate',
        source: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84989',
    }),
    freezeWindowSetTimeframeEndDateCalendar: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSetTimeframeEndDateCalendar',
        source: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84990',
    }),
    freezeWindowSetTimeframeNext: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSetTimeframeNext',
        source: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84991',
    }),
    freezeWindowSetTimeframeBack: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSetTimeframeBack',
        source: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84992',
    }),
    freezeWindowSetTimeframeExit: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowSetTimeframeExit',
        source: FreezeWindowsScreens.freezeWindowSetTimeframeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84993',
    }),
    freezeWindowCreateFreezeWindowCreate: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowCreateFreezeWindowCreate',
        source: FreezeWindowsScreens.freezeWindowCreateFreezeWindowScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85105',
    }),
    freezeWindowCreateFreezeWindowBack: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowCreateFreezeWindowBack',
        source: FreezeWindowsScreens.freezeWindowCreateFreezeWindowScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85106',
    }),
    freezeWindowCreateFreezeWindowExit: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowCreateFreezeWindowExit',
        source: FreezeWindowsScreens.freezeWindowCreateFreezeWindowScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85107',
    }),
    freezeWindowHomeSupportLink: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowHomeSupportLink',
        source: FreezeWindowsScreens.freezeWindowHomeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84838',
    }),
    freezeWindowHomeRemove: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowHomeRemove',
        source: FreezeWindowsScreens.freezeWindowHomeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86162',
    }),
    freezeWindowHomeRemoveConfirm: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowHomeRemoveConfirm',
        source: FreezeWindowsScreens.freezeWindowRemoveModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86168',
    }),
    freezeWindowHomeRemoveCancel: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowHomeRemoveCancel',
        source: FreezeWindowsScreens.freezeWindowRemoveModal,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86169',
    }),
    freezeWindowHomeCreate: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowHomeCreate',
        source: FreezeWindowsScreens.freezeWindowHomeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87203',
    }),
    freezeWindowHomeView: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowHomeView',
        source: FreezeWindowsScreens.freezeWindowHomeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87480',
    }),
    freezeWindowHomeSidebarClose: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'freezeWindowHomeSidebarClose',
        source: FreezeWindowsScreens.freezeWindowHomeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87481',
    }),
  },
  track: {
    freezeWindowCreateFreezeWindow: ({ orgId, workspaceIds }: { orgId: string; workspaceIds: string }): TrackEvent => ({
      data: {
        action: TrackAction.Created,
        actionSubject: TrackActionSubject.FreezeWindowCreateFreezeWindow,
        actionSubjectId: orgId,
        source: FreezeWindowsScreens.freezeWindowCreateFreezeWindowScreen,
        attributes: {
          orgId,
          workspaceIds,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85208',
    }),
  },
  operational: {},
};
